/**
 * Application Constants
 *
 * Central configuration for the application.
 * Modify these values to match your backend API.
 */

/**
 * API Configuration
 */

const API_URL = import.meta.env.VITE_API_URL;

export const API_CONFIG = {
  BASE_URL: API_URL || 'http://localhost:5000/api',
  TIMEOUT: 30000,
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
} as const;

/**
 * Identity provider (SSO) configuration. Values come from build-time
 * environment variables — names use generic `VITE_AUTH_*` to avoid
 * exposing the underlying technology in the bundle.
 */
export const SSO_CONFIG = {
  URL: import.meta.env.VITE_AUTH_URL || '',
  REALM: import.meta.env.VITE_AUTH_REALM || '',
  CLIENT_ID: import.meta.env.VITE_AUTH_CLIENT_ID || '',
  PORTAL_URL: import.meta.env.VITE_PORTAL_URL || '',
  APP_ID: 'notify',
} as const;

/**
 * Standard JWT claim URIs. Roles arrive in the `resource_access.{client}.roles`
 * claim plus the duplicated short `role` claim that the backend writes.
 */
export const JWT_CLAIMS = {
  ROLE: 'role',
  NAME_IDENTIFIER: 'sub',
} as const;

/**
 * Pagination Configuration
 */
export const PAGINATION_CONFIG = {
  DEFAULT_PAGE_SIZE: 25,
  PAGE_SIZE_OPTIONS: [10, 25, 50, 100],
  MAX_PAGE_SIZE: 1000,
} as const;

/**
 * Status Colors for UI
 *
 * Consistent color coding for different statuses.
 */
export const STATUS_COLORS = {
  active: '#4caf50',
  inactive: '#9e9e9e',
  pending: '#ff9800',
  error: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3',
} as const;

/**
 * Language Options
 */
export const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
] as const;

/**
 * Date Formats
 */
export const DATE_FORMATS = {
  DATE: 'YYYY-MM-DD',
  TIME: 'HH:mm:ss',
  DATETIME: 'YYYY-MM-DD HH:mm:ss',
  DISPLAY_DATE: 'MMM D, YYYY',
  DISPLAY_DATETIME: 'MMM D, YYYY h:mm A',
} as const;

// ============================================
// DERIVED TYPES
// ============================================

export type LanguageCode = (typeof LANGUAGE_OPTIONS)[number]['value'];
